/**
 * Phase 1.7 — dashboard right rail: overdue + upcoming tasks.
 *
 * Reads from DashboardSummary.overdueTasks and upcomingTasks. Each row is
 * a Link to the (future) task page; for now anchors to /logs since that
 * surfaces task-derived events.
 */

import { Calendar, AlertCircle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import { RailSection } from '@/components/v2/layout';
import { EmptyState } from '@/components/v2';
import type { DashboardSummary } from '@shared/types';

export interface TodayTasksRailProps {
  summary: DashboardSummary;
}

export function TodayTasksRail({ summary }: TodayTasksRailProps): JSX.Element {
  const overdue = summary.overdueTasks.items;
  const upcoming = summary.upcomingTasks.items.slice(0, 5);
  return (
    <>
      {overdue.length > 0 ? (
        <RailSection
          title="Overdue"
          action={
            <span className="rounded-full bg-clay/10 px-2 py-0.5 font-mono text-2xs uppercase tracking-wider text-clay dark:bg-clay-dark/20 dark:text-clay-dark">
              {overdue.length}
            </span>
          }
        >
          <ul className="space-y-2">
            {overdue.slice(0, 5).map((t) => (
              <li key={t.id} className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-clay dark:text-clay-dark" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{t.title}</p>
                  <p className="text-xs text-muted-foreground">
                    Due {format(parseISO(t.due_date), 'MMM d')}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </RailSection>
      ) : null}

      <RailSection
        title="Upcoming"
        action={
          <Link to="/logs" className="text-xs text-burnt-orange hover:underline dark:text-burnt-orange-dark">
            View all →
          </Link>
        }
      >
        {upcoming.length === 0 ? (
          <EmptyState
            variant="card"
            icon={Calendar}
            title="Nothing scheduled"
            description="Your week is clear."
          />
        ) : (
          <ul className="space-y-2">
            {upcoming.map((t) => (
              <li key={t.id} className="flex items-start gap-2">
                <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{t.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {format(parseISO(t.due_date), 'EEE, MMM d')}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </RailSection>
    </>
  );
}
